<script lang="ts">
  import { parseLines } from '@streepaf/shared/parse'
  import { t } from '../../lib/i18n/index.svelte.js'
  import type { ListStore } from '../../lib/state/listStore.svelte.js'
  import { toasts } from '../../lib/state/toasts.svelte.js'
  import Icon from '../ui/Icon.svelte'

  interface Props {
    store: ListStore
  }
  const { store }: Props = $props()

  let value = $state('')
  let input = $state<HTMLInputElement | null>(null)

  export function focus(): void {
    input?.focus()
  }

  function submit(): void {
    const text = value.trim()
    if (!text) return
    const drafts = parseLines(text)
    if (drafts.length === 0) return
    store.addItems(drafts)
    value = ''
    input?.focus()
  }

  function onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault()
      submit()
    } else if (event.key === 'Escape') {
      input?.blur()
    }
  }

  /** A pasted block becomes many rows in one go; a single line just fills in. */
  function onPaste(event: ClipboardEvent): void {
    const text = event.clipboardData?.getData('text/plain') ?? ''
    if (!text.includes('\n')) return
    event.preventDefault()
    const drafts = parseLines(text)
    if (drafts.length === 0) return
    const created = store.addItems(drafts)
    value = ''
    toasts.show(t('toast_items_added', { count: created.length }), {
      action: {
        label: t('undo'),
        run: () => {
          for (const item of created) store.deleteItem(item.id, { silent: true })
        },
      },
    })
  }
</script>

<div class="add" id="add-row">
  <span class="plus" aria-hidden="true"><Icon name="plus" size={20} /></span>
  <input
    bind:this={input}
    bind:value
    type="text"
    placeholder={t('add_item_placeholder')}
    aria-label={t('add_item')}
    enterkeyhint="enter"
    autocapitalize="sentences"
    autocomplete="off"
    spellcheck="true"
    onkeydown={onKeydown}
    onpaste={onPaste}
  />
  {#if value.trim()}
    <button class="go" onclick={submit} aria-label={t('add_item')}>
      <Icon name="plus" size={18} />
    </button>
  {/if}
</div>

<style>
  .add {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    min-height: 52px;
    padding-right: var(--space-2);
    border-radius: var(--radius);
    background: var(--surface);
  }
  .plus {
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    flex: 0 0 44px;
    color: var(--fg-faint);
  }
  input {
    flex: 1;
    min-width: 0;
    padding: var(--space-2) 0;
    border: none;
    background: transparent;
    font-size: 17px;
  }
  input:focus {
    outline: none;
  }
  input::placeholder {
    color: var(--fg-faint);
  }
  .go {
    display: grid;
    place-items: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: var(--radius-sm);
    background: var(--accent);
    color: var(--accent-fg);
  }
</style>
